import React, { useState, useRef } from "react";
import PageHeader from "../components/PageHeader";
import { heroImages } from "../data/images";
import { submitToWeb3Forms } from "../utils/web3forms";

const initialForm = { name:"", company:"", email:"", country:"", product:"Vegetables", volume:"", port:"", message:"" };

export default function ContactPage() {
  const [form, setForm] = useState(initialForm);
  const [botcheck, setBotcheck] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const startedAtRef = useRef(Date.now());

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await submitToWeb3Forms({
        data: form,
        subject: `Export Inquiry — ${form.product} — ${form.name}`,
        from_name: form.name || "Website Inquiry",
        botcheck,
        startedAt: startedAtRef.current,
      });
      setStatus("success");
      setForm(initialForm);
      setTimeout(() => setStatus("idle"), 5000);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <>
      <PageHeader eyebrow="Get in Touch"
        title="Let's build a" titleAccent="reliable trade route."
        subtitle="Share your requirements — volume, destination, target pricing — and our export team will come back within one business day with a tailored proposal."
        image={heroImages.spiceMarket} emoji="📮" height="h-[48vh] md:h-[56vh]"
        rightContent={
          <div className="glass-strong shimmer-border rounded-3xl p-6 w-full max-w-sm">
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mb-5">Reach Us Directly</div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Email</div>
                  <a href="mailto:info@myriadgreenenergy.in" className="text-sm text-stone-300 hover:text-amber-400 transition break-all">info@myriadgreenenergy.in</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center shrink-0 mt-0.5">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2l2 5-3 2a12 12 0 006 6l2-3 5 2v2a2 2 0 01-2 2A17 17 0 013 5z" />
                  </svg>
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Phone</div>
                  <a href="tel:+919960739464" className="text-sm text-stone-300 hover:text-amber-400 transition">+91 99607 39464</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0 mt-0.5">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" /><circle cx="12" cy="12" r="9" strokeLinecap="round" />
                  </svg>
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Response Time</div>
                  <div className="text-sm text-stone-300">Within 1 business day</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center shrink-0 mt-0.5">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-7.5 8-13a8 8 0 10-16 0c0 5.5 8 13 8 13z" /><circle cx="12" cy="9" r="2.5" />
                  </svg>
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Office Hours</div>
                  <div className="text-sm text-stone-300">Mon – Fri · 09:00 – 19:00 IST</div>
                </div>
              </li>
            </ul>
          </div>
        } />

      <section className="relative py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-8">

            {/* Info cards */}
            <div className="lg:col-span-4 space-y-4">
              <InfoCard gradient="from-emerald-600 to-emerald-700" label="Email" value="info@myriadgreenenergy.in"
                href="mailto:info@myriadgreenenergy.in"
                icon={<path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />} />
              <InfoCard gradient="from-indigo-300 to-indigo-600" label="Phone" value="+91 99607 39464"
                href="tel:+919960739464"
                icon={<path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2l2 5-3 2a12 12 0 006 6l2-3 5 2v2a2 2 0 01-2 2A17 17 0 013 5z" />} />
              <InfoCard gradient="from-slate-300 to-slate-500" label="Head Office" value="Sr.No 138 Plot No 89 Hissa No 3, Siddhi Green Phase 4, Fursungi Road, Bhinde Safalya House, Pune, Maharashtra – 412308, India"
                icon={<><path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-7.5 8-13a8 8 0 10-16 0c0 5.5 8 13 8 13z" /><circle cx="12" cy="9" r="2.5" strokeLinecap="round" strokeLinejoin="round" /></>} />

              <div className="glass rounded-3xl p-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mb-3">Working Hours</div>
                <ul className="text-sm text-stone-600 space-y-1.5">
                  {[["Mon – Fri","09:00 – 19:00 IST"],["Saturday","10:00 – 14:00 IST"],["Sunday","By appointment"]].map(([d,h]) => (
                    <li key={d} className="flex justify-between">
                      <span className="text-stone-400">{d}</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              <form onSubmit={onSubmit} className="glass-strong shimmer-border rounded-3xl p-6 md:p-10 space-y-5">
                {/* Honeypot — hidden from users, bots tend to fill it */}
                <input
                  type="checkbox"
                  name="botcheck"
                  tabIndex="-1"
                  autoComplete="off"
                  checked={botcheck}
                  onChange={(e) => setBotcheck(e.target.checked)}
                  style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
                  aria-hidden="true"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your Name"   name="name"    value={form.name}    onChange={handle} required />
                  <Field label="Company"     name="company" value={form.company} onChange={handle} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email" type="email" name="email" value={form.email} onChange={handle} required />
                  <Field label="Country"     name="country" value={form.country} onChange={handle} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Volume (MT / container)" name="volume" value={form.volume} onChange={handle} placeholder="e.g. 1 x 40' reefer" />
                  <Field label="Destination Port"        name="port"   value={form.port}   onChange={handle} placeholder="e.g. Rotterdam" />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-stone-400 block mb-3">Product Interest</label>
                  <div className="flex flex-wrap gap-2">
                    {["Vegetables","Spices","Fresh Fruits","Processed","Other"].map(p => (
                      <button key={p} type="button" onClick={() => setForm({...form, product:p})}
                        className={`px-4 py-2 rounded-xl text-sm transition ${form.product === p ? "bg-gradient-to-br from-emerald-700 to-emerald-600 text-white font-medium shadow-lg" : "glass-pill text-stone-600 hover:bg-stone-900/8"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-stone-400 block mb-2">Message</label>
                  <textarea name="message" rows={5} value={form.message} onChange={handle}
                    placeholder="Tell us about packaging needs, timelines, certifications required..."
                    className="w-full bg-stone-900/5 border border-stone-900/10 focus:border-emerald-600/50 focus:bg-stone-900/8 rounded-2xl px-4 py-3 text-sm text-stone-900 placeholder-stone-400 outline-none transition" />
                </div>

                {status === "error" && (
                  <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                    {errorMsg}
                  </p>
                )}

                <button type="submit"
                  disabled={status === "sending" || status === "success"}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-sm font-medium text-white bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-700 hover:from-emerald-600 hover:to-emerald-700 transition shadow-xl shadow-emerald-600/25 disabled:opacity-70 disabled:cursor-not-allowed">
                  {status === "success" ? (
                    "✓ Thank you — we'll be in touch within 24 h"
                  ) : status === "sending" ? (
                    <>
                      <svg viewBox="0 0 24 24" className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v4m0 8v4m8-8h-4M8 12H4m13.66-5.66l-2.83 2.83M9.17 14.83l-2.83 2.83m0-11.32l2.83 2.83m5.66 5.66l2.83 2.83" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Inquiry
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, value, onChange, type="text", required, placeholder }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-stone-400 block mb-2">
        {label}{required && <span className="text-amber-300"> *</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full bg-stone-900/5 border border-stone-900/10 focus:border-emerald-600/50 focus:bg-stone-900/8 rounded-2xl px-4 py-3 text-sm text-stone-900 placeholder-stone-400 outline-none transition" />
    </div>
  );
}

function InfoCard({ gradient, label, value, href, icon }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap href={href} className={`glass rounded-3xl p-5 flex items-center gap-4 ${href ? "glass-hover" : ""}`}>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0`}>
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg>
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400">{label}</div>
        <div className="font-medium text-stone-400">{value}</div>
      </div>
    </Wrap>
  );
}
